import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  trackClickMessage,
  trackClickRecommendation,
  trackExpertButtonClick,
  trackOfferMessageTeaserShow,
} from './tracking';
import { retrySendMessageById } from '../../actions/sendMessage/retrySendMessageById';
import { OfferMessage } from '../../components/OfferMessage';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { currentOpenChatIDSelector } from '../../selectors';
import { TMessage, TOutcomingStatus } from '../../types/messages';
import { setViewOffer } from '../../thunks/chatbot/feedback/setViewOffer';
import { EReaction, IMessageOfferSchema } from '../../repositories/cian-chat-bot/entities/offer/MessageOfferSchema';
import { applyOfferReaction } from '../../thunks/chatbot';
import { useApplicationContext } from '../../utils/applicationContext';
import { trackLike } from '../ChatMessageContainer/tracking';
import { useOfferMessageTooltip } from './provider';
import { useObserver } from '../../hooks/useObserver';
import { IOfferForClientSchema } from '../../repositories/chats/entities/for_client/OfferForClientSchema';

type IBaseOfferSchema = IMessageOfferSchema & IOfferForClientSchema;

type TOfferMessageContainerProps = {
  isOutcoming: boolean;
  isMessageFailed: boolean;
  messageId: string;
  message: TMessage & { status: TOutcomingStatus };
  textNode: React.ReactNode | null;
};

export const OfferMessageContainer: React.FC<TOfferMessageContainerProps> = ({
  isMessageFailed,
  isOutcoming,
  message,
  messageId,
  textNode,
}) => {
  const { config } = useApplicationContext();
  const dispatch = useDispatch();

  const isChatbotGPTEnabled = config.get<boolean>('frontend-chats.chatbotGPT.Enabled') || false;
  const isExpertButtonEnabled = config.get<boolean>('frontend-chats.expertSearchEnable') || false;

  const [reaction, setReaction] = React.useState<EReaction | undefined>(undefined);
  const chatId = useSelector(currentOpenChatIDSelector);
  const offer = message?.offers && (message?.offers[0] as IBaseOfferSchema);
  const isChatbotChat = Boolean(chatId === CHATBOT_ID);

  const { showTooltip, hideTooltip, offerMessageTooltipState, rootRef } = useOfferMessageTooltip();
  const rootElementRef = React.useRef<HTMLDivElement | null>(null);
  const messageRef = React.useRef<HTMLDivElement>(null);

  useObserver({
    rootElementRef,
    targetElementRef: messageRef,
    condition: isChatbotChat,
    handler: () => {
      if (
        isExpertButtonEnabled &&
        !offerMessageTooltipState?.isVisible &&
        offer &&
        offer.realtyOfferId &&
        !isOutcoming
      ) {
        showTooltip(offer.realtyOfferId);
      }
    },
  });

  const handleCloseTooltip = () => {
    hideTooltip();
  };

  React.useEffect(() => {
    if (offer) {
      setReaction(offer.reaction);
    }
  }, [offer]);

  const handleReload = React.useCallback(() => {
    dispatch(retrySendMessageById(chatId as string, messageId));
  }, [chatId, dispatch, messageId]);

  const handleTrackOfferMessageTeaserShow = React.useCallback(() => {
    if (offer) {
      trackOfferMessageTeaserShow({
        offerId: offer.realtyOfferId,
        block: isChatbotChat ? 'Chat_cian_helper' : 'Chat_snippet',
      });
    }
  }, [isChatbotChat, offer]);

  const onClick = React.useCallback(() => {
    if (isChatbotChat && offer) {
      dispatch(setViewOffer(offer.realtyOfferId));
      trackClickRecommendation(offer.realtyOfferId, message.communicationId, offer.isExpertEnabled ?? false);
    } else if (offer && chatId) {
      trackClickMessage(offer.realtyOfferId, chatId, messageId, message.communicationId);
    }
  }, [isChatbotChat, offer]);

  const handleLikeClick = React.useCallback(() => {
    if (!offer) {
      return;
    }

    const nextReaction = reaction !== EReaction.Like ? EReaction.Like : undefined;

    setReaction(nextReaction);
    trackLike(offer.realtyOfferId, nextReaction, message.communicationId);
    dispatch(
      applyOfferReaction({
        offerId: offer.realtyOfferId,
        reaction: nextReaction,
      }),
    );
  }, [dispatch, offer, reaction]);

  const handleExpertButtonClick = () => {
    if (offer) {
      trackExpertButtonClick(offer.realtyOfferId, message.communicationId);
    }
  };

  if (!offer) {
    return null;
  }

  const { formattedAddress, formattedInfo, formattedPrice, formattedOldPrice, url, photoUrls } = offer;

  const param = `source=${isChatbotChat ? 'chat_bot' : 'chats'}`;
  const shareUrl = url.includes('?') ? `${url}&${param}` : `${url}?${param}`;

  return (
    <ActionAfterViewObserver
      percentVisible={0.5}
      callback={handleTrackOfferMessageTeaserShow}
      triggerOnce={false}
      callbackDelay={1000}
    >
      <OfferMessage
        isOutcoming={isOutcoming}
        isChatbotChat={isChatbotChat}
        isChatbotEnabled={isChatbotGPTEnabled}
        reaction={reaction}
        formattedAddress={formattedAddress}
        formattedInfo={formattedInfo}
        formattedPrice={formattedPrice}
        formattedOldPrice={formattedOldPrice}
        formattedText={textNode}
        shareUrl={shareUrl}
        photoUrl={photoUrls}
        withReload={isMessageFailed}
        messageRef={messageRef}
        visibleTooltip={offerMessageTooltipState?.offerId === offer.realtyOfferId}
        tooltipPortal={rootRef}
        onReload={handleReload}
        onClick={onClick}
        onLike={handleLikeClick}
        setCloseTooltip={handleCloseTooltip}
        onExpertButton={handleExpertButtonClick}
      />
    </ActionAfterViewObserver>
  );
};
